import { useState } from 'react';
import {
  PARTNER_NEWS_IMAGE_SLOTS,
  partnerNewsImageFormatOf,
  type PartnerNewsAdmin
} from '@bliks-io/contracts';
import {
  useCreatePartnerNews,
  useRemovePartnerNewsImage,
  useUpdatePartnerNews,
  useUploadPartnerNewsImage
} from '@/pages/admin/api/mutations/usePartnerNews';
import {
  toCreateNewsRequest,
  toUpdateNewsRequest,
  type NewsForm,
  type PendingImages
} from './newsForm';

/**
 * Saves text, then pictures, then the publish state, in that order: news only
 * goes live once the pictures it shows are in storage.
 */
export function useSaveNews() {
  const createNews = useCreatePartnerNews();
  const updateNews = useUpdatePartnerNews();
  const uploadImage = useUploadPartnerNewsImage();
  const removeImage = useRemovePartnerNewsImage();
  const [isSaving, setIsSaving] = useState(false);

  const save = async ({
    existing,
    values,
    pending,
    publish
  }: {
    existing: PartnerNewsAdmin | null;
    values: NewsForm;
    pending: PendingImages;
    publish: boolean;
  }): Promise<PartnerNewsAdmin> => {
    setIsSaving(true);
    try {
      let news = existing
        ? await updateNews.mutateAsync({ id: existing._id, data: toUpdateNewsRequest(values) })
        : await createNews.mutateAsync(toCreateNewsRequest(values));

      for (const slot of pending.removals) {
        news = await removeImage.mutateAsync({ id: news._id, slot });
      }
      for (const slot of PARTNER_NEWS_IMAGE_SLOTS) {
        const file = pending.uploads[slot];
        const format = file && partnerNewsImageFormatOf(file.type);
        if (!file || !format) continue;
        news = await uploadImage.mutateAsync({ id: news._id, slot, format, file });
      }
      if (news.published !== publish) {
        news = await updateNews.mutateAsync({ id: news._id, data: { published: publish } });
      }
      return news;
    } finally {
      setIsSaving(false);
    }
  };

  return { save, isSaving };
}
